import React, { useMemo } from 'react';
import {
  format, addDays, subDays, addWeeks, subWeeks,
  startOfWeek, endOfWeek, isSameDay, isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { sessionLabel, sessionsOn, useClinicHours } from '@/hooks/useClinicHours';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Apt {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: string;
  doctor_id?: string;
  patient_name?: string;
  patient_age?: number;
  patient_gender?: string;
}

interface Props {
  view: 'day' | 'week';
  date: Date;
  appointments: Apt[];
  isFrontDesk: boolean;
  checkingInId?: string | null;
  onNavigate: (d: Date) => void;
  onNewAppointment: (date: string, time?: string) => void;
  onEditAppointment: (apt: Apt) => void;
  onCheckIn: (apt: Apt) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Day view hours when the clinic hasn't set its hours (evening clinics run late; booking offers slots to 10 PM)
const START_HOUR = 8;
const END_HOUR   = 22;
const HOUR_PX    = 64; // px per hour in day view

const STATUS_STYLE: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  scheduled:  { border: 'border-muted-foreground/40', bg: 'bg-muted',       text: 'text-foreground',             dot: 'bg-muted-foreground' },
  checked_in: { border: 'border-primary',             bg: 'bg-primary/10',  text: 'text-primary',                dot: 'bg-primary'          },
  in_progress:{ border: 'border-primary',             bg: 'bg-primary/10',  text: 'text-primary',                dot: 'bg-primary'          },
  completed:  { border: 'border-success/60',          bg: 'bg-success/10',  text: 'text-success',                dot: 'bg-success'          },
  no_show:    { border: 'border-highlight-foreground/50', bg: 'bg-highlight', text: 'text-highlight-foreground',  dot: 'bg-highlight-foreground' },
  cancelled:  { border: 'border-border',              bg: 'bg-card',        text: 'text-muted-foreground line-through', dot: 'bg-muted-foreground/40' },
};

function styleFor(status: string) {
  return STATUS_STYLE[status] ?? STATUS_STYLE.scheduled;
}

function fmtTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const d = new Date(); d.setHours(h, m);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function parseTime(t: string) {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return { hour: h || 0, minutes: m || 0 };
}

function aptTopPx(time: string, start: number, end: number) {
  const { hour, minutes } = parseTime(time);
  const clamped = Math.max(start, Math.min(end - 1, hour));
  return (clamped - start + minutes / 60) * HOUR_PX;
}

// ─── Nav header shared by both views ─────────────────────────────────────────

function NavHeader({
  label, onPrev, onNext, onToday,
}: { label: string; onPrev: () => void; onNext: () => void; onToday: () => void }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} className="w-8 px-0" aria-label="Previous">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-semibold text-foreground min-w-[11rem] sm:min-w-[200px] text-center tabular-nums">{label}</span>
        <Button variant="outline" size="sm" onClick={onNext} className="w-8 px-0" aria-label="Next">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <Button variant="outline" size="sm" onClick={onToday}>
        Today
      </Button>
    </div>
  );
}

// ─── Appointment pill (shared) ────────────────────────────────────────────────

function AptPill({
  apt, compact, isFrontDesk, checkingInId,
  onClick, onCheckIn,
}: {
  apt: Apt; compact?: boolean; isFrontDesk: boolean;
  checkingInId?: string | null;
  onClick: () => void; onCheckIn: (e: React.MouseEvent) => void;
}) {
  const s = styleFor(apt.status);
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      className={`rounded border-l-[3px] px-2 py-1 cursor-pointer overflow-hidden hover:ring-1 hover:ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${s.border} ${s.bg}`}
    >
      <p className={`text-xs font-semibold truncate capitalize ${s.text}`}>
        {apt.patient_name || 'Patient'}
      </p>
      <p className="text-2xs text-muted-foreground truncate">
        <span className="font-mono">{fmtTime(apt.time)}</span>{!compact && apt.reason ? ` · ${apt.reason}` : ''}
      </p>
      {/* Check-in is for today only; the server refuses any other day */}
      {!compact && apt.status === 'scheduled' && isFrontDesk && isToday(new Date(`${apt.date.slice(0, 10)}T00:00:00`)) && (
        <button
          onClick={onCheckIn}
          disabled={checkingInId === apt.id}
          className={`touch-target mt-1 inline-flex items-center gap-1 h-6 px-2 rounded border border-primary/40 bg-card text-2xs font-semibold text-primary hover:bg-primary/10 ${checkingInId === apt.id ? 'opacity-50' : ''}`}
        >
          <UserCheck className="w-3 h-3" />
          {checkingInId === apt.id ? 'Checking in…' : 'Check in'}
        </button>
      )}
    </div>
  );
}

// ─── Day view ─────────────────────────────────────────────────────────────────

function DayView({ date, appointments, isFrontDesk, checkingInId, onNavigate, onNewAppointment, onEditAppointment, onCheckIn }: Omit<Props, 'view'>) {
  const dateStr = format(date, 'yyyy-MM-dd');

  const dayApts = useMemo(() =>
    appointments.filter(a => a.date.split('T')[0] === dateStr),
  [appointments, dateStr]);

  // The clinic's hours that day (undefined: not set); the grid covers them and every booking that day
  const sessions = sessionsOn(useClinicHours(), dateStr);
  const { start, end } = useMemo(() => {
    if (!sessions?.length) return { start: START_HOUR, end: END_HOUR };
    let start = Math.min(...sessions.map(s => parseTime(s.start).hour));
    let end = Math.max(...sessions.map(s => { const t = parseTime(s.end); return t.minutes ? t.hour + 1 : t.hour; }));
    for (const a of dayApts) {
      const h = parseTime(a.time).hour;
      start = Math.min(start, h); end = Math.max(end, h + 1);
    }
    return { start, end };
  }, [sessions, dayApts]);
  const hours = Array.from({ length: end - start }, (_, i) => start + i);
  const openAt = (h: number) => !sessions
    || sessions.some(s => `${String(h).padStart(2, '0')}:59` >= s.start && `${String(h).padStart(2, '0')}:00` < s.end);

  return (
    <div>
      <NavHeader
        label={format(date, 'EEEE, d MMMM yyyy')}
        onPrev={() => onNavigate(subDays(date, 1))}
        onNext={() => onNavigate(addDays(date, 1))}
        onToday={() => onNavigate(new Date())}
      />
      {sessions && (
        <p className="-mt-2 mb-3 text-xs text-muted-foreground">
          {sessions.length ? `Open ${sessions.map(sessionLabel).join(', ')}` : 'Clinic closed this day'}
        </p>
      )}

      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {/* Scrollable grid */}
        <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
          <div className="relative" style={{ height: `${hours.length * HOUR_PX}px` }}>

            {/* Hour lines + labels; hours the clinic is closed are greyed */}
            {hours.map(h => (
              <div
                key={h}
                className={`absolute left-0 right-0 border-t border-border/40 flex ${openAt(h) ? '' : 'bg-muted/50'}`}
                style={{ top: `${(h - start) * HOUR_PX}px`, height: `${HOUR_PX}px` }}
              >
                <div className="w-14 flex-shrink-0 pr-2 pt-1 text-right">
                  <span className="font-mono text-2xs text-muted-foreground">
                    {h % 12 || 12}{h < 12 ? 'am' : 'pm'}
                  </span>
                </div>
                {/* Click to add */}
                <div
                  className="flex-1 hover:bg-primary/5 cursor-pointer transition-colors group"
                  onClick={() => onNewAppointment(dateStr, `${String(h).padStart(2, '0')}:00`)}
                >
                  <Plus className="w-3.5 h-3.5 text-primary opacity-0 group-hover:opacity-100 touch:opacity-40 mt-1 ml-1 transition-opacity" />
                </div>
              </div>
            ))}

            {/* Appointment blocks */}
            {dayApts.map(apt => {
              const top = aptTopPx(apt.time, start, end);
              return (
                <div
                  key={apt.id}
                  className="absolute left-16 right-3"
                  style={{ top: `${top}px`, minHeight: `${HOUR_PX * 0.6}px`, zIndex: 1 }}
                >
                  <AptPill
                    apt={apt}
                    isFrontDesk={isFrontDesk}
                    checkingInId={checkingInId}
                    onClick={() => onEditAppointment(apt)}
                    onCheckIn={e => { e.stopPropagation(); onCheckIn(apt); }}
                  />
                </div>
              );
            })}

            {dayApts.length === 0 && (
              <div
                className="absolute inset-0 left-14 flex items-center justify-center text-sm text-muted-foreground pointer-events-none"
              >
                No appointments — tap a time to add one
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Week view ────────────────────────────────────────────────────────────────

function WeekView({ date, appointments, isFrontDesk, checkingInId, onNavigate, onNewAppointment, onEditAppointment, onCheckIn }: Omit<Props, 'view'>) {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekDays  = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const byDay = useMemo(() => {
    const map: Record<string, Apt[]> = {};
    weekDays.forEach(d => { map[format(d, 'yyyy-MM-dd')] = []; });
    appointments.forEach(a => {
      const key = a.date.split('T')[0];
      if (map[key]) map[key].push(a);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [appointments, weekStart]);

  const label = `${format(weekStart, 'd MMM')} – ${format(addDays(weekStart, 6), 'd MMM yyyy')}`;
  const clinicHours = useClinicHours();

  return (
    <div>
      <NavHeader
        label={label}
        onPrev={() => onNavigate(subWeeks(date, 1))}
        onNext={() => onNavigate(addWeeks(date, 1))}
        onToday={() => onNavigate(new Date())}
      />

      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className="overflow-x-auto">
        <div className="min-w-[44rem]">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {weekDays.map(d => {
            const today = isToday(d);
            return (
              <div
                key={d.toISOString()}
                className={`px-2 py-2.5 text-center border-r border-border/50 last:border-0 cursor-pointer hover:bg-muted/40 transition-colors ${today ? 'bg-primary/5' : ''}`}
                onClick={() => onNavigate(d)}
              >
                <p className="text-2xs text-muted-foreground uppercase tracking-wide">
                  {format(d, 'EEE')}
                </p>
                <p className={`text-sm font-semibold tabular-nums mt-0.5 w-7 h-7 rounded-full flex items-center justify-center mx-auto ${
                  today ? 'bg-primary text-primary-foreground' : 'text-foreground'
                }`}>
                  {format(d, 'd')}
                </p>
                {sessionsOn(clinicHours, d)?.length === 0 && (
                  <p className="text-2xs text-muted-foreground mt-0.5">Closed</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Day columns */}
        <div className="grid grid-cols-7 min-h-[400px]" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
          {weekDays.map(d => {
            const key = format(d, 'yyyy-MM-dd');
            const dayApts = byDay[key] ?? [];
            const today = isToday(d);
            return (
              <div
                key={key}
                className={`border-r border-border/50 last:border-0 p-1.5 min-h-[200px] ${today ? 'bg-primary/5' : ''}`}
              >
                <div className="space-y-1">
                  {dayApts.map(apt => (
                    <AptPill
                      key={apt.id}
                      apt={apt}
                      compact
                      isFrontDesk={isFrontDesk}
                      checkingInId={checkingInId}
                      onClick={() => onEditAppointment(apt)}
                      onCheckIn={e => { e.stopPropagation(); onCheckIn(apt); }}
                    />
                  ))}
                </div>
                {/* Add slot */}
                <button
                  onClick={() => onNewAppointment(key)}
                  className="mt-1 w-full text-center h-7 touch:h-10 rounded text-2xs text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors opacity-0 hover:opacity-100 focus-visible:opacity-100 touch:opacity-100"
                  aria-label={`Add appointment on ${format(d, 'EEEE d MMM')}`}
                >
                  + Add
                </button>
              </div>
            );
          })}
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function AppointmentCalendar(props: Props) {
  if (props.view === 'day') return <DayView {...props} />;
  return <WeekView {...props} />;
}
